import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import {
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import { isEmail } from '../../../../util/StringUtils';
import { SignatureLabelErrorMessage } from '../../SignatureSettings/SignatureSettings';
import {
    closeChangeCredentialsDialog,
    getProjectSignatureSettings,
    resetAreSignatureCredentialsSaved,
    saveProjectSignatureSettings,
    UserSignatureChangeTypeEnum,
} from '../../../../redux/project/sliceProjectUserSignatures';
import { useParams } from 'react-router-dom';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, py: 1 },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
};
const ctaButtonSx: SxProps = {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: headingFontBold,
    lineHeight: 'normal',
    padding: '9px 12px',
    color: vars.white,
};

const initialMainState: ISignMeCredentialsMainState = {
    signmeUsername: '',
    signmePAK: '',
};

const initialErrorState: ISignMeCredentialsErrorState = {
    signmeUsername: false,
    signmePAK: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'signMeCredentialsDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const { replicaId } = useParams();

    const isDialogOpen = useSelector(
        (_store: IAppReduxStore) =>
            _store.projectUserSignatureSettings.isChangeCredentialsDialogOpen,
    );

    const { loading: isLoading, areCredentialsSaved } = useSelector(
        (_store: IAppReduxStore) => _store.projectUserSignatureSettings,
    );

    useEffect(() => {
        if (areCredentialsSaved) {
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });

            dispatch(resetAreSignatureCredentialsSaved());
            onClose();
        }
    }, [areCredentialsSaved]);

    const [mainState, setMainState] = useState<ISignMeCredentialsMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<ISignMeCredentialsErrorState>(initialErrorState);

    const handleChange = ({ field, value }: { field: string; value: string | number }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState({
            ...mainState,
            [field]: value,
        });
    };

    const resetToInitialState = () => {
        setMainState(initialMainState);
        setShowErrorState(initialErrorState);
    };

    const onClose = () => {
        resetToInitialState();

        dispatch(getProjectSignatureSettings({ replicaId }));
        dispatch(closeChangeCredentialsDialog());
    };

    const validateInput = () => {
        const pakRegex = /^PAK-[A-Z0-9]{25}$/;

        setShowErrorState({
            signmeUsername: !isEmail(mainState.signmeUsername),
            signmePAK: !pakRegex.test(mainState.signmePAK),
        });
        return isEmail(mainState.signmeUsername) && pakRegex.test(mainState.signmePAK);
    };
    const onUpdate = () => {
        if (validateInput()) {
            dispatch(
                saveProjectSignatureSettings({
                    properties: {
                        ...mainState,
                    },
                    replicaId,
                    typeOfChange: UserSignatureChangeTypeEnum.Credentials,
                }),
            );
        }
    };
    return (
        <DialogFrame open={isDialogOpen} customSx={{ maxHeight: '365px' }}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <Stack spacing={'12px'}>
                    <FormControl>
                        <FormLabel sx={formLabelSx}>{t('signmeUsername')}</FormLabel>
                        <TextField
                            disabled={isLoading}
                            sx={textFieldSx}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange({ field: 'signmeUsername', value: e.target.value })
                            }
                            placeholder={t('signmeUsernamePlaceholder')}
                            value={mainState.signmeUsername}
                            error={showErrorState.signmeUsername}
                        />
                        {showErrorState.signmeUsername &&
                            SignatureLabelErrorMessage(t('errorInputSignmeUsername'))}
                    </FormControl>
                    <FormControl>
                        <FormLabel sx={formLabelSx}>{t('signmePAK')}</FormLabel>
                        <TextField
                            disabled={isLoading}
                            sx={textFieldSx}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange({ field: 'signmePAK', value: e.target.value })
                            }
                            placeholder={t('signmePAKPlaceholder')}
                            value={mainState.signmePAK}
                            error={showErrorState.signmePAK}
                        />
                        {showErrorState.signmePAK &&
                            SignatureLabelErrorMessage(t('errorInputSignmePAK'))}
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1, borderTop: `1px solid ${vars.lightGray}` }}>
                <Button
                    variant={'text'}
                    onClick={onClose}
                    sx={{ ...ctaButtonSx, color: vars.font }}
                    disabled={isLoading}
                >
                    {t('ctaCancel')}
                </Button>
                <Button
                    variant={'contained'}
                    onClick={onUpdate}
                    sx={ctaButtonSx}
                    disabled={isLoading}
                >
                    {t('ctaUpdate')}
                </Button>
            </DialogActions>
        </DialogFrame>
    );
};

export interface ISignMeCredentialsMainState {
    signmeUsername: string;
    signmePAK: string;
}

export interface ISignMeCredentialsErrorState {
    signmeUsername: boolean;
    signmePAK: boolean;
}
